/**
 * PermitSignPage — what you get when you scan the QR code on the barrier door.
 * No login: the precautions, the dates, who signed it and who to call.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldAlert, ShieldCheck, Phone, CheckCircle2, XCircle } from "lucide-react";
import { API } from "@/App";

const CLASS_BG = {
  I: "bg-emerald-700", II: "bg-blue-700", III: "bg-amber-600",
  "III/IV": "bg-red-700", IV: "bg-red-700",
};

const Row = ({ k, v }) => (
  <div className="flex gap-3 py-2 border-b border-slate-200 last:border-0">
    <span className="w-32 shrink-0 text-[10px] font-black uppercase tracking-[0.12em] text-slate-500 pt-0.5">{k}</span>
    <span className="text-sm text-slate-800">{v || "—"}</span>
  </div>
);

export default function PermitSignPage() {
  const { token } = useParams();
  const [permit, setPermit] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/icra-permits/public/${token}`);
      setPermit(r.data.permit);
    } catch (e) {
      setError(e?.response?.data?.detail || "This permit link is no longer valid.");
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6" data-testid="permit-error">
        <div className="max-w-md text-center bg-white rounded-2xl ring-1 ring-slate-200 p-8">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="font-bold text-slate-900">{error}</p>
          <p className="text-sm text-slate-500 mt-2">
            Contact the facility Safety department before entering the work area.
          </p>
        </div>
      </div>
    );
  }

  if (!permit) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const cls = permit.precaution_class;
  const active = permit.status === "active" && !permit.is_expired;

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="permit-public-page">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="bg-white rounded-2xl ring-1 ring-slate-200 overflow-hidden shadow-sm">
          <div className="px-6 pt-6 pb-4 border-b-4 border-slate-900">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">
              Infection Control Risk Assessment · Construction Permit
            </p>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight mt-1">{permit.number}</h1>
            <p className="text-sm text-slate-600">{permit.project_name} · {permit.facility}</p>
          </div>

          <div className={`${CLASS_BG[cls] || "bg-blue-700"} text-white text-center py-5 px-4`}>
            <p className="text-[11px] font-black uppercase tracking-[0.25em]">Class of Precautions</p>
            <p className="text-5xl font-black leading-none my-1">{cls}</p>
            <p className="text-[11px] font-bold uppercase tracking-[0.15em]">
              Activity Type {permit.activity_type} × Patient Risk Group {permit.patient_risk_group}
            </p>
          </div>

          <div className={`px-6 py-3 text-center text-sm font-black uppercase tracking-[0.15em] ${
            active ? "bg-emerald-50 text-emerald-800" : "bg-red-600 text-white"}`}
            data-testid="permit-status-band">
            {active ? (
              <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4" />Permit active</span>
            ) : (
              <span className="inline-flex items-center gap-2"><ShieldAlert className="w-4 h-4" />
                {permit.is_expired ? "Permit expired — do not enter" : `Permit ${permit.status.replace(/_/g, " ")} — not authorized`}
              </span>
            )}
          </div>

          <div className="px-6 py-4">
            <Row k="Work" v={permit.work_description} />
            <Row k="Location" v={[permit.building, permit.floor && `Floor ${permit.floor}`,
              permit.department, permit.room_numbers].filter(Boolean).join(" · ")} />
            <Row k="Permit dates" v={`${permit.start_date || "—"} → ${permit.end_date || "—"}`} />
            <Row k="Work hours" v={permit.work_hours} />
            <Row k="Contractor" v={permit.responsible_company} />
            <Row k="Containment" v={[permit.barrier_type,
              permit.negative_air_units ? `${permit.negative_air_units} negative air unit(s)` : null,
              permit.hepa_filtration ? "HEPA filtration" : null].filter(Boolean).join(" · ")} />
            <Row k="Debris route" v={permit.debris_route} />
            <Row k="Traffic route" v={permit.traffic_route} />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-5">
            <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500 mb-2">
              Required during construction
            </p>
            <ul className="space-y-1.5">
              {(permit.precautions?.during || []).map((l, i) => (
                <li key={i} className="text-[13px] text-slate-700 leading-snug">• {l}</li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-5">
            <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500 mb-2">
              Required upon completion
            </p>
            <ul className="space-y-1.5">
              {(permit.precautions?.after || []).map((l, i) => (
                <li key={i} className="text-[13px] text-slate-700 leading-snug">• {l}</li>
              ))}
            </ul>
          </div>
        </div>

        {permit.additional_requirements ? (
          <div className="bg-amber-50 ring-1 ring-amber-300 rounded-2xl p-5">
            <p className="text-[10px] font-black uppercase tracking-[0.14em] text-amber-800 mb-1">
              Additional site requirements
            </p>
            <p className="text-sm text-amber-900 whitespace-pre-wrap">{permit.additional_requirements}</p>
          </div>
        ) : null}

        <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-5">
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500 mb-3">Signatures on file</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {[["infection_prevention", "Infection Prevention"],
              ["safety_facilities", "Safety / Facilities"],
              ["contractor_foreman", "Contractor foreman"]].map(([key, label]) => {
              const s = permit.signatures?.[key];
              return (
                <div key={key} className="rounded-lg bg-slate-50 ring-1 ring-slate-200 p-3">
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</p>
                  {s?.name ? (
                    <p className="text-sm font-bold text-slate-900 mt-1 inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />{s.name}
                    </p>
                  ) : (
                    <p className="text-sm text-red-600 font-bold mt-1">Not signed</p>
                  )}
                  {s?.signed_at ? <p className="text-[11px] text-slate-500">{s.signed_at.slice(0, 10)}</p> : null}
                </div>
              );
            })}
          </div>
        </div>

        {permit.ilsm_required ? (
          <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-5">
            <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500 mb-2">
              Interim Life Safety Measures — recent daily checks
            </p>
            {(permit.ilsm_checks || []).length === 0 ? (
              <p className="text-sm text-red-600 font-bold">No ILSM check logged yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {permit.ilsm_checks.slice().reverse().map((c, i) => (
                  <span key={i} className={`text-[11px] font-bold px-2 py-1 rounded-full ring-1 ${
                    c.all_pass ? "bg-emerald-50 text-emerald-700 ring-emerald-300"
                      : "bg-amber-50 text-amber-800 ring-amber-300"}`}>
                    {c.check_date} {c.all_pass ? "pass" : "deficiency"}
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : null}

        <div className="bg-slate-900 text-white rounded-2xl p-5">
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400 mb-2">
            Report a containment breach immediately
          </p>
          <div className="space-y-1 text-sm">
            {permit.contractor_foreman ? (
              <p className="inline-flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-amber-400" />
                Foreman {permit.contractor_foreman}
                {permit.foreman_phone ? ` · ${permit.foreman_phone}` : ""}</p>
            ) : null}
            {permit.infection_prevention_contact ? (
              <p className="block">Infection Prevention: {permit.infection_prevention_contact}</p>
            ) : null}
            {permit.safety_contact ? (
              <p className="block">Safety / Facilities: {permit.safety_contact}</p>
            ) : null}
          </div>
          <p className="text-[11px] text-slate-400 mt-3">{permit.company_address}</p>
        </div>
      </div>
    </div>
  );
}
